import { useState, useMemo } from "react";
import {
  Triangle,
  Pencil,
  Trash2,
  Pin,
  Reply,
  MessageSquare,
} from "lucide-react";
import { useContextMenu } from "../../../contexts/RightClickMenuContext";
import { FormattedMessage } from "../messages";
import MentionText from "../input/MentionText";
import { ENTITY_FULL_NAME } from "../../../../api/config";
import ShareInviteCard from "../messages/sharing/ShareInviteCard";
import SharedArticleCard from "../messages/sharing/SharedArticleCard";

const AI_THEME = {
  accent: "#0EA5E9",
  bubbleBg: "#0EA5E9/5",
  bubbleBorder: "#0EA5E9/15",
  avatarBg: "#0EA5E9/10",
  nameColor: "sky-500",
};

const QUICK_EMOJIS = ["👍", "❤️", "😂", "🎉", "👀", "🔥"];

const MENTION_TEST = /@(\w[\w\s]*?)(?=\s|$|[.,!?;:])/;
const CITATION_MARKER = /(\[\d+\])/g;

const TeamAiContent = ({
  content,
  citations,
  isStreaming,
  onCitationClick,
}) => {
  if (!content && isStreaming) {
    return (
      <span className="inline-flex items-center">
        <span className="ai-streaming-cursor" />
      </span>
    );
  }
  if (!content) return null;

  const citationMap = {};
  for (const c of citations || []) {
    citationMap[c.index] = c;
  }

  const parts = content.split(CITATION_MARKER);

  return (
    <span>
      {parts.map((part, i) => {
        const match = part.match(/^\[(\d+)\]$/);
        if (match) {
          const idx = parseInt(match[1], 10);
          const citation = citationMap[idx];
          if (citation) {
            return (
              <button
                key={`cite-${i}`}
                onClick={() => onCitationClick?.(citation)}
                className="inline-flex items-center justify-center min-w-[20px] h-[18px] px-1 mx-0.5 rounded text-[10px] font-medium bg-[#0EA5E9]/15 text-[#0EA5E9] hover:bg-[#0EA5E9]/25 transition-colors cursor-pointer align-baseline"
                title={
                  citation.title || citation.source_name || `Source ${idx}`
                }
              >
                {idx}
              </button>
            );
          }
          return <span key={`cite-${i}`}>{part}</span>;
        }

        if (!part) return null;
        if (MENTION_TEST.test(part)) {
          return <span key={`text-${i}`}>{part}</span>;
        }
        return (
          <FormattedMessage
            key={`text-${i}`}
            text={part}
            citations={[]}
            citationMap={{}}
            citationInteractions={{}}
          />
        );
      })}
      {isStreaming && (
        <span className="inline-flex items-center ml-0.5">
          <span className="ai-streaming-cursor" />
        </span>
      )}
      <style>{`
        .ai-streaming-cursor {
          display: inline-block;
          width: 5px;
          height: 15px;
          background: #0EA5E9;
          opacity: 0.7;
          border-radius: 1px;
          animation: ai-cursor-blink 0.8s ease-in-out infinite;
          vertical-align: text-bottom;
          margin-bottom: 1px;
        }
        @keyframes ai-cursor-blink {
          0%, 100% { opacity: 0.7; }
          50% { opacity: 0.1; }
        }
      `}</style>
    </span>
  );
};

const MessageContent = ({
  content,
  userUuid,
  members,
  msg,
  onCitationClick,
}) => {
  if (!content) {
    if (msg?._streaming) {
      return (
        <TeamAiContent
          content=""
          citations={[]}
          isStreaming
          onCitationClick={onCitationClick}
        />
      );
    }
    return null;
  }

  const isAi = msg?.type === "ai" || msg?.sender_uuid === "ai_assistant";
  const hasCitations =
    isAi && Array.isArray(msg?.citations) && msg.citations.length > 0;

  if (isAi && (hasCitations || msg?._streaming)) {
    return (
      <TeamAiContent
        content={content}
        citations={msg?.citations || []}
        isStreaming={msg?._streaming}
        onCitationClick={onCitationClick}
      />
    );
  }

  if (MENTION_TEST.test(content)) {
    return (
      <MentionText
        text={content}
        currentUserUuid={userUuid}
        members={members}
      />
    );
  }
  return (
    <FormattedMessage
      text={content}
      citations={[]}
      citationMap={{}}
      citationInteractions={{}}
    />
  );
};

const TeamThreadView = ({
  messages = [],
  userUuid,
  onlineUsers = new Set(),
  members = {},
  lastReadTimestamp,
  onEdit,
  onDelete,
  onReact,
  onPin,
  onReply,
  onArticleClick,
  onShareInviteClick,
  onCitationClick,
}) => {
  const [editingId, setEditingId] = useState(null);

  console.log("mounted");

  const replyMap = useMemo(() => {
    const map = {};
    for (const msg of messages) {
      const parentId = msg.reply_to?.message_id;
      if (!parentId) continue;
      if (!map[parentId]) map[parentId] = { count: 0, hasUnread: false };
      map[parentId].count++;
      if (!msg.deleted && !msg.read_by?.includes(userUuid)) {
        map[parentId].hasUnread = true;
      }
    }
    return map;
  }, [messages, userUuid]);

  useContextMenu("team-message", [
    {
      label: "Reply",
      icon: Reply,
      action: (payload) => {
        if (payload?.message_id) {
          onReply?.(payload.message_id);
        }
      },
      hidden: (payload) => payload?.deleted,
    },
    {
      label: "Pin Message",
      icon: Pin,
      action: (payload) => {
        if (payload?.message_id) {
          onPin?.(payload.message_id, !payload.pinned);
        }
      },
      hidden: (payload) => payload?.deleted,
    },
    {
      label: "Edit Message",
      icon: Pencil,
      action: (payload) => {
        if (payload?.message_id) {
          setEditingId(payload.message_id);
        }
      },
      hidden: (payload) =>
        payload?.sender_uuid !== userUuid || payload?.deleted,
    },
    {
      label: "Delete Message",
      icon: Trash2,
      variant: "danger",
      action: (payload) => {
        if (payload?.message_id) {
          onDelete?.(payload.message_id);
        }
      },
      hidden: (payload) =>
        payload?.sender_uuid !== userUuid || payload?.deleted,
    },
  ]);

  if (messages.length === 0) return [];

  const elements = [];
  let prevSender = null;
  let prevTimestamp = 0;
  let separatorInserted = false;

  for (let i = 0; i < messages.length; i++) {
    const msg = messages[i];

    if (msg.type === "system") {
      prevSender = null;
      elements.push(
        <div key={msg.message_id} className="flex justify-center py-2">
          <span className="text-[11px] font-light text-mutedText italic">
            {msg.content}
          </span>
        </div>,
      );
      continue;
    }

    if (msg.type === "share_invite") {
      const shareMember = members[msg.sender_uuid] || {};
      const shareDisplayName =
        msg.sender_name || shareMember.display_name || "Unknown";
      const shareInitial = (shareDisplayName[0] || "?").toUpperCase();
      const shareIsOwn = msg.sender_uuid === userUuid;
      const shareTimeStr = _formatTime(msg.timestamp);
      const shareIsGrouped =
        msg.sender_uuid === prevSender && msg.timestamp - prevTimestamp < 120;

      prevSender = msg.sender_uuid;
      prevTimestamp = msg.timestamp;

      elements.push(
        <div key={msg.message_id} className="pt-3">
          <div className="flex gap-x-3">
            <div className="w-8 flex-shrink-0">
              {!shareIsGrouped && (
                <div
                  className={[
                    "w-8 h-8 rounded-full flex items-center justify-center text-[13px] font-light relative",
                    shareIsOwn
                      ? "bg-velBlue/20 text-velBlue"
                      : "bg-mutedText/20 text-mutedText",
                  ].join(" ")}
                >
                  {shareInitial}
                  {onlineUsers.has(msg.sender_uuid) && (
                    <div className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 bg-activeCase rounded-full border-2 border-surface" />
                  )}
                </div>
              )}
            </div>
            <div className="flex-1 min-w-0 max-w-[720px]">
              {!shareIsGrouped && (
                <div className="flex items-baseline gap-x-2 mb-0.5">
                  <span
                    className={[
                      "text-[13px] font-normal",
                      shareIsOwn ? "text-velBlue" : "text-mainText",
                    ].join(" ")}
                  >
                    {shareDisplayName}
                  </span>
                  <span className="text-[11px] font-light text-mutedText/60">
                    {shareTimeStr}
                  </span>
                </div>
              )}
              <ShareInviteCard
                msg={msg}
                userUuid={userUuid}
                onClick={onShareInviteClick}
              />
            </div>
          </div>
        </div>,
      );
      continue;
    }

    if (msg.type === "shared_article") {
      const articleMember = members[msg.sender_uuid] || {};
      const articleDisplayName =
        msg.sender_name || articleMember.display_name || "Unknown";
      const articleInitial = (articleDisplayName[0] || "?").toUpperCase();
      const articleIsOwn = msg.sender_uuid === userUuid;
      const articleTimeStr = _formatTime(msg.timestamp);
      const articleIsGrouped =
        msg.sender_uuid === prevSender && msg.timestamp - prevTimestamp < 120;

      prevSender = msg.sender_uuid;
      prevTimestamp = msg.timestamp;

      elements.push(
        <div key={msg.message_id} className="pt-3">
          <div className="flex gap-x-3">
            <div className="w-8 flex-shrink-0">
              {!articleIsGrouped && (
                <div
                  className={[
                    "w-8 h-8 rounded-full flex items-center justify-center text-[13px] font-light relative",
                    articleIsOwn
                      ? "bg-velBlue/20 text-velBlue"
                      : "bg-mutedText/20 text-mutedText",
                  ].join(" ")}
                >
                  {articleInitial}
                  {onlineUsers.has(msg.sender_uuid) && (
                    <div className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 bg-activeCase rounded-full border-2 border-surface" />
                  )}
                </div>
              )}
            </div>
            <div className="flex-1 min-w-0 max-w-[720px]">
              {!articleIsGrouped && (
                <div className="flex items-baseline gap-x-2 mb-0.5">
                  <span
                    className={[
                      "text-[13px] font-normal",
                      articleIsOwn ? "text-velBlue" : "text-mainText",
                    ].join(" ")}
                  >
                    {articleDisplayName}
                  </span>
                  <span className="text-[11px] font-light text-mutedText/60">
                    {articleTimeStr}
                  </span>
                </div>
              )}
              <SharedArticleCard
                msg={msg}
                userUuid={userUuid}
                onClickArticle={onArticleClick}
              />
            </div>
          </div>
        </div>,
      );
      continue;
    }

    const isOwn = msg.sender_uuid === userUuid;
    const isAI = msg.sender_uuid === "ai_assistant";
    const timeDiff = msg.timestamp - prevTimestamp;
    const isGrouped = msg.sender_uuid === prevSender && timeDiff < 120;

    const timeStr = _formatTime(msg.timestamp);
    const member = members[msg.sender_uuid] || {};
    const displayName = msg.sender_name || member.display_name || "Unknown";
    const avatarInitial = (displayName[0] || "?").toUpperCase();

    const replyData = replyMap[msg.message_id];
    const replyCount = replyData?.count || 0;
    const hasUnreadReplies = replyData?.hasUnread || false;

    if (
      !separatorInserted &&
      lastReadTimestamp &&
      !isOwn &&
      !msg.deleted &&
      msg.timestamp > lastReadTimestamp
    ) {
      const prevMsg = i > 0 ? messages[i - 1] : null;
      const prevWasRead =
        !prevMsg ||
        prevMsg.sender_uuid === userUuid ||
        (prevMsg.timestamp || 0) <= lastReadTimestamp;

      if (prevWasRead) {
        separatorInserted = true;
        const unreadCount = messages
          .slice(i)
          .filter(
            (m) =>
              m.timestamp > lastReadTimestamp &&
              m.sender_uuid !== userUuid &&
              !m.deleted &&
              m.type !== "system",
          ).length;

        elements.push(
          <div
            key="unread-separator"
            id="unread-separator"
            className="flex items-center gap-x-3 py-2"
          >
            <div className="flex-1 h-px bg-velBlue/50" />
            <span className="text-[11px] font-light text-velBlue whitespace-nowrap">
              {unreadCount} new message{unreadCount !== 1 ? "s" : ""}
            </span>
            <div className="flex-1 h-px bg-velBlue/50" />
          </div>,
        );
      }
    }

    prevSender = msg.sender_uuid;
    prevTimestamp = msg.timestamp;

    const isLastOwnMsg =
      isOwn &&
      (i === messages.length - 1 || messages[i + 1]?.sender_uuid !== userUuid);
    const readBy = (msg.read_by || []).filter((u) => u !== userUuid);
    const readByNames = readBy
      .map((u) => {
        const m = members[u];
        return m?.display_name || m?.first_name || null;
      })
      .filter(Boolean);

    const isEditing = editingId === msg.message_id;

    const reactions = msg.reactions || {};
    const hasReactions = Object.keys(reactions).length > 0;

    elements.push(
      <div
        key={msg.message_id}
        data-context-menu={
          msg.type !== "system" && !msg.deleted ? "team-message" : undefined
        }
        data-context-payload={
          msg.type !== "system" && !msg.deleted
            ? JSON.stringify({
                message_id: msg.message_id,
                sender_uuid: msg.sender_uuid,
                deleted: msg.deleted,
                pinned: msg.pinned || false,
              })
            : undefined
        }
        className={[
          "group relative -mx-2 px-2 rounded-md",
          isGrouped ? "pt-0.5" : "pt-3",
          "transition-colors",
          isAI
            ? msg._streaming
              ? "bg-[#0EA5E9]/[0.06] border-l-2 border-[#0EA5E9]/40"
              : "bg-[#0EA5E9]/[0.04] border-l-2 border-[#0EA5E9]/20"
            : hasUnreadReplies
              ? "bg-velBlue/5 border-l-2 border-velBlue"
              : "hover:bg-mainText/[0.04]",
        ].join(" ")}
      >
        {msg.pinned && !msg.deleted && (
          <div className="flex items-center gap-x-1 mb-0.5 ml-11">
            <Pin className="h-3 w-3 text-sunColor" strokeWidth={2} />
            <span className="text-[10px] font-light text-sunColor">Pinned</span>
          </div>
        )}

        {/* Hover toolbar.
            Position adapts to whether the row is grouped:
              - Non-grouped row (`pt-3` above content): `-top-4`. The
                row has ~12px of pad above content, so the toolbar can
                sit further up without colliding with the previous row.
              - Grouped row (`pt-0.5` above content): `-top-3`. There
                are only ~2px of pad, so a higher toolbar would overlap
                the previous row's text. -top-3 keeps the toolbar's
                bottom edge visually inside this row's space.
            Bg: solid `bg-surface` (no alpha) so the row hover bg
            doesn't bleed through and produce a half-cut look. */}
        {!msg.deleted && !isEditing && (
          <div
            className={[
              "absolute right-4 hidden group-hover:flex items-center gap-x-0.5",
              "bg-surface border border-surfaceBorder rounded-full shadow-lg px-1 py-0.5 z-10",
              isGrouped ? "-top-3" : "-top-4",
            ].join(" ")}
          >
            {QUICK_EMOJIS.map((emoji) => (
              <button
                key={emoji}
                onClick={() => onReact?.(msg.message_id, emoji)}
                className="w-6 h-6 flex items-center justify-center rounded-full hover:bg-mainText/[0.06] transition-colors text-[14px] cursor-pointer"
                title={emoji}
              >
                {emoji}
              </button>
            ))}
            <div className="w-px h-4 bg-dropdownDivider mx-0.5" />
            <button
              onClick={() => onReply?.(msg.message_id)}
              className="w-6 h-6 flex items-center justify-center rounded-full hover:bg-velBlue/10 transition-colors cursor-pointer"
              title="Reply"
            >
              <Reply
                className="h-3.5 w-3.5 text-mutedText hover:text-velBlue transition-colors"
                strokeWidth={2}
              />
            </button>
          </div>
        )}

        <div className="flex gap-x-3">
          <div className="w-8 flex-shrink-0">
            {!isGrouped && (
              <div
                className={[
                  "w-8 h-8 rounded-full flex items-center justify-center text-[13px] font-light relative",
                  isAI
                    ? "bg-[#0EA5E9]/10 text-[#0EA5E9]"
                    : isOwn
                      ? "bg-velBlue/20 text-velBlue"
                      : "bg-mutedText/20 text-mutedText",
                ].join(" ")}
              >
                {isAI ? (
                  <Triangle
                    className="h-4 w-4"
                    fill="#0EA5E9"
                    strokeWidth={0}
                  />
                ) : (
                  avatarInitial
                )}
                {!isAI && onlineUsers.has(msg.sender_uuid) && (
                  <div className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 bg-activeCase rounded-full border-2 border-surface" />
                )}
              </div>
            )}
          </div>

          <div className="flex-1 min-w-0 max-w-[720px]">
            {!isGrouped && (
              <div className="flex items-baseline gap-x-2 mb-0.5">
                <span
                  className={[
                    "text-[13px] font-normal",
                    isAI
                      ? "text-[#0EA5E9]"
                      : isOwn
                        ? "text-velBlue"
                        : "text-mainText",
                  ].join(" ")}
                >
                  {isAI ? ENTITY_FULL_NAME : displayName}
                </span>
                <span className="text-[11px] font-light text-mutedText/60">
                  {timeStr}
                </span>
              </div>
            )}

            {msg.deleted ? (
              <p className="text-sm font-normal italic text-mutedText/60 leading-relaxed">
                This message was deleted
              </p>
            ) : isEditing ? (
              <div className="flex items-center gap-x-2">
                <input
                  autoFocus
                  className="flex-1 text-sm font-normal px-2 py-1 rounded-lg border border-inputBorder bg-transparent text-mainText focus:outline-none focus:ring-2 focus:ring-inputFocus/60"
                  defaultValue={msg.content}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      const val = e.target.value.trim();
                      if (val && val !== msg.content) {
                        onEdit?.(msg.message_id, val);
                      }
                      setEditingId(null);
                    }
                    if (e.key === "Escape") setEditingId(null);
                  }}
                />
                <button
                  onClick={() => setEditingId(null)}
                  className="text-[11px] font-light text-mutedText hover:text-mainText transition-colors cursor-pointer"
                >
                  Cancel
                </button>
              </div>
            ) : (
              <div className="text-sm font-normal text-mainText leading-relaxed">
                <MessageContent
                  content={msg.content}
                  userUuid={userUuid}
                  members={members}
                  msg={msg}
                  onCitationClick={onCitationClick}
                />
                {msg.edited && (
                  <span className="ml-1.5 text-[10px] font-light text-mutedText/60 italic">
                    (edited)
                  </span>
                )}
              </div>
            )}

            {hasReactions && !msg.deleted && (
              <div className="flex flex-wrap gap-1 mt-1">
                {Object.entries(reactions).map(([emoji, uuids]) => {
                  const count = uuids.length;
                  const iReacted = uuids.includes(userUuid);
                  return (
                    <button
                      key={emoji}
                      onClick={() => onReact?.(msg.message_id, emoji)}
                      className={[
                        "inline-flex items-center gap-x-1 px-1.5 py-0.5 rounded-full text-[12px] border transition-colors cursor-pointer",
                        iReacted
                          ? "bg-velBlue/10 border-velBlue/30"
                          : "bg-transparent border-inputBorder hover:bg-mainText/[0.04]",
                      ].join(" ")}
                      title={uuids
                        .map(
                          (u) =>
                            members[u]?.display_name ||
                            members[u]?.first_name ||
                            "Unknown",
                        )
                        .join(", ")}
                    >
                      <span>{emoji}</span>
                      <span className="text-[10px] text-mutedText font-light">
                        {count}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}

            {/* "View N replies" link — only when replies exist. The
                "create a reply" affordance is in the hover toolbar. */}
            {!msg.deleted && replyCount > 0 && (
              <button
                onClick={() => onReply?.(msg.message_id)}
                className={[
                  "flex items-center gap-x-1.5 mt-1.5 px-2 py-1 rounded-full text-[11px] font-light transition-colors cursor-pointer",
                  hasUnreadReplies
                    ? "text-velBlue bg-velBlue/10 hover:bg-velBlue/20"
                    : "text-mutedText bg-surface/80 hover:bg-mainText/[0.04]",
                ].join(" ")}
              >
                <MessageSquare className="h-3 w-3" strokeWidth={2} />
                <span>
                  {replyCount} repl{replyCount === 1 ? "y" : "ies"}
                </span>
                {hasUnreadReplies && (
                  <span className="w-1.5 h-1.5 rounded-full bg-velBlue ml-0.5" />
                )}
              </button>
            )}

            {isLastOwnMsg && readByNames.length > 0 && !msg.deleted && (
              <div className="mt-1 text-[10px] font-light text-mutedText/60">
                <span className="mr-1">✓✓</span>
                {readByNames.length <= 3
                  ? readByNames.join(", ")
                  : `${readByNames.slice(0, 2).join(", ")} +${readByNames.length - 2}`}
              </div>
            )}
          </div>

          {isGrouped && (
            <span className="text-[10px] font-light text-mutedText/60 opacity-0 group-hover:opacity-100 transition-opacity self-center flex-shrink-0">
              {timeStr}
            </span>
          )}
        </div>
      </div>,
    );
  }

  return elements;
};

export const renderTeamThread = (props) => <TeamThreadView {...props} />;

function _formatTime(ts) {
  try {
    const d = new Date(ts * 1000);
    return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  } catch {
    return "";
  }
}

export default renderTeamThread;
